import { split } from 'lodash'
import CryptoJS from 'crypto-js'
const CLIENT_ENCRYPTION_SECRET = process.env.REACT_APP_CLIENT_ENCRYPTION_SECRET

/**
 * Parses an encrypted text string into its IV and ciphertext components.
 *
 * @param {string} encryptedText - The encrypted text in the format "iv:ciphertext".
 * @returns {{ iv: CryptoJS.lib.WordArray, ciphertext: CryptoJS.lib.WordArray }} An object containing the parsed IV and ciphertext as CryptoJS WordArray instances.
 */
export const parseEncryptedText = (encryptedText) => {
  const parts = split(encryptedText, ':')
  const iv = CryptoJS.enc.Hex.parse(parts.shift())
  const ciphertext = CryptoJS.enc.Hex.parse(parts.join(':'))
  return { iv, ciphertext }
}

/**
 * Decrypts an AES-encrypted ciphertext using CBC mode and PKCS7 padding.
 *
 * @param {string} ciphertext - The encrypted data to decrypt.
 * @param {CryptoJS.lib.WordArray} iv - The initialization vector used during encryption.
 * @param {string} secret - The secret key (in hex format) used for decryption.
 * @returns {string} The decrypted plaintext as a UTF-8 string.
 */
export const aesDecrypt = (ciphertext, iv, secret) => {
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext },
    CryptoJS.enc.Hex.parse(secret),
    { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
  )
  return decrypted.toString(CryptoJS.enc.Utf8)
}

/**
 * Decrypts an encrypted text using AES decryption.
 *
 * @param {string} encryptedText - The encrypted text to decrypt. Should be a string containing both IV and ciphertext.
 * @returns {string} The decrypted plaintext.
 * @throws {Error} If decryption fails due to invalid key, IV, or ciphertext.
 *
 * @example
 * // Example usage:
 * const decrypted = decrypt('iv:ciphertext');
 * console.log(decrypted); // Output: original plaintext
 */
export const decrypt = (encryptedText) => {
  try {
    const { iv, ciphertext } = parseEncryptedText(encryptedText)
    return JSON.parse(aesDecrypt(ciphertext, iv, CLIENT_ENCRYPTION_SECRET))
  } catch (error) {
    console.error('Decryption failed:', error)
    return {}
  }
}
